import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { BookOpen, Clock, Award, PlayCircle, Download, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : 'http://localhost:8001/api';

const MyCourses = () => {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchEnrolledCourses();
  }, [token, navigate]);

  const fetchEnrolledCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses/enrolled`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnrolledCourses(response.data.courses || []);
    } catch (error) {
      console.error('Error fetching enrolled courses:', error);
      toast.error('Failed to load your courses');
    } finally {
      setLoading(false);
    }
  };

  const handleContinueLearning = (courseId) => {
    navigate(`/course-player/${courseId}`);
  };

  const fetchMyCourses = fetchEnrolledCourses;

  const handleDownloadCertificate = async (courseId) => {
    try {
      toast.info('Generating certificate...', { duration: 2000 });
      
      const response = await axios.post(
        `${API}/certificates/${courseId}/generate`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Convert base64 to blob and download
      const pdfBase64 = response.data.pdf_base64;
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificate-${response.data.certificate_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Certificate downloaded successfully!');
      
      // Refresh to show updated certificate status
      fetchMyCourses();
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error(error.response?.data?.detail || 'Failed to download certificate');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading your courses...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (enrolledCourses.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Enrolled Courses</h2>
            <p className="text-gray-600 mb-6">Start learning by enrolling in a course!</p>
            <Button onClick={() => navigate('/courses')}>
              Browse Courses
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">My Courses</h1>
          <p className="text-gray-600">Continue your learning journey</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {enrolledCourses.map((enrollment) => {
            const course = enrollment.course || {};
            const progress = enrollment.progress || 0;
            const isCompleted = progress >= 100;

            return (
              <Card key={course.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  {course.image_url && (
                    <div className="w-full h-48 rounded-md overflow-hidden mb-4">
                      <img
                        src={course.image_url}
                        alt={course.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-xl">{course.title}</CardTitle>
                    {isCompleted && (
                      <Badge className="ml-2">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Completed
                      </Badge>
                    )}
                  </div>
                  
                  <CardDescription>
                    {course.description?.substring(0, 100)}...
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Progress</span>
                      <span className="text-sm font-medium text-indigo-600">{progress.toFixed(0)}%</span>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                    {course.duration && (
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>{course.duration}</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Button
                      onClick={() => handleContinueLearning(course.id)}
                      className="w-full"
                    >
                      <PlayCircle className="h-4 w-4 mr-2" />
                      {isCompleted ? 'Review Course' : 'Continue Learning'}
                    </Button>

                    {isCompleted && (
                      <Button
                        variant="outline"
                        onClick={() => handleDownloadCertificate(course.id)}
                        className="w-full"
                      >
                        <Award className="h-4 w-4 mr-2" />
                        {enrollment.certificate_id ? 'Download Certificate' : 'Get Certificate'}
                      </Button>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t text-xs text-gray-500">
                    Enrolled: {new Date(enrollment.enrolled_at).toLocaleDateString()}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MyCourses;